import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { Send, Trash2, ImagePlus, X, BarChart3, Check } from 'lucide-react';
import { toast } from 'sonner';
import { UserBadgesLoader } from '@/components/UserBadges';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMOJIS = [
  { key: 'heart', icon: '❤️' },
  { key: 'like', icon: '👍' },
  { key: 'lightning', icon: '⚡' },
  { key: 'party', icon: '🎉' },
  { key: 'eagle', icon: '🦅' },
];

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return `${diff} seg`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function PostCard({ post, user, onDelete, onImageClick }) {
  const [reactions, setReactions] = useState({});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    fetchReactions();
    fetchComments();
  }, [post.id]);

  const fetchReactions = async () => {
    try {
      const res = await axios.get(`${API}/posts/${post.id}/reactions`);
      setReactions(res.data);
    } catch {}
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(res.data);
    } catch {}
  };

  const handleReaction = async (emoji) => {
    try {
      await axios.post(`${API}/posts/${post.id}/react`, { emoji });
      fetchReactions();
    } catch {}
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    const words = newComment.trim().split(/\s+/);
    if (words.length > 50) {
      toast.error('Máximo 50 palabras');
      return;
    }
    try {
      await axios.post(`${API}/posts/${post.id}/comments`, { content: newComment.trim() });
      setNewComment('');
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      fetchComments();
    } catch {}
  };

  const userReacted = (emoji) => {
    return reactions[emoji]?.users?.includes(user?.id);
  };

  return (
    <div className="glass-panel rounded-lg p-6" data-testid={`post-${post.id}`}>
      {/* Author Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <img src="/ricardo_paz.png" alt="Ricardopazfx" className="w-11 h-11 rounded-full object-cover object-top border-2 border-purple-500/60" />
          <div>
            <p className="text-emerald-50 font-bold">{post.author_name}</p>
            <p className="text-emerald-100/50 text-sm">{post.author_role} · {timeAgo(post.created_at)}</p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <button onClick={() => onDelete(post.id)} className="text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`post-delete-${post.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="text-emerald-100/90 whitespace-pre-wrap mb-4 leading-relaxed">{post.content}</div>

      {/* Images */}
      {post.images?.length > 0 && (
        <div className={`grid gap-2 mb-4 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {post.images.map((img, i) => (
            <img key={i} src={img} alt="" onClick={() => onImageClick(img)} className="w-full rounded-lg border border-emerald-500/20 object-cover max-h-80 cursor-pointer hover:opacity-90 transition-opacity" />
          ))}
        </div>
      )}

      {/* Reactions */}
      <div className="flex items-center space-x-1 mb-3 border-t border-emerald-500/10 pt-3">
        {EMOJIS.map((e) => (
          <button
            key={e.key}
            onClick={() => handleReaction(e.key)}
            className={`flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm transition-all ${
              userReacted(e.key)
                ? 'bg-emerald-500/20 border border-emerald-500/40'
                : 'bg-black/30 border border-emerald-500/10 hover:bg-emerald-500/10'
            }`}
            data-testid={`reaction-${e.key}-${post.id}`}
          >
            <span>{e.icon}</span>
            {reactions[e.key]?.count > 0 && (
              <span className="text-emerald-100/70 text-xs">{reactions[e.key].count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Comments toggle */}
      <button
        onClick={() => setShowComments(!showComments)}
        className="text-emerald-400/70 text-sm hover:text-emerald-400 mb-2"
        data-testid={`toggle-comments-${post.id}`}
      >
        {comments.length > 0 ? `${comments.length} comentario${comments.length > 1 ? 's' : ''}` : 'Comentar'}
      </button>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-emerald-500/10 pt-3 space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="flex items-start space-x-2 group" data-testid={`comment-${c.id}`}>
              <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold flex-shrink-0">
                {c.user_name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-emerald-50 text-sm">
                  <span className="font-bold">{c.user_name}</span>{' '}
                  <span className="text-emerald-100/70">{c.content}</span>
                </p>
                <p className="text-emerald-100/30 text-xs">{timeAgo(c.created_at)}</p>
              </div>
              {(c.user_id === user?.id || user?.role === 'admin') && (
                <button onClick={() => handleDeleteComment(c.id)} className="text-emerald-100/20 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}

          {/* New Comment Input */}
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleComment()}
              placeholder="Escribe un comentario (máx. 50 palabras)..."
              maxLength={300}
              className="flex-1 bg-black/40 border border-emerald-500/20 rounded-full px-4 py-2 text-sm text-emerald-50 placeholder:text-emerald-100/30 focus:outline-none focus:border-emerald-500/50"
              data-testid={`comment-input-${post.id}`}
            />
            <button
              onClick={handleComment}
              disabled={!newComment.trim()}
              className="text-emerald-400 hover:text-emerald-300 disabled:opacity-30"
              data-testid={`comment-submit-${post.id}`}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Timestamp */}
      <p className="text-emerald-100/30 text-xs mt-3">
        {new Date(post.created_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })}
      </p>
    </div>
  );
}

function PollCard({ poll, user, onDelete, onVote }) {
  const totalVotes = poll.options?.reduce((acc, o) => acc + (o.votes || 0), 0) || 0;
  const myVotes = poll.voters?.[user?.id] || [];
  const hasVoted = myVotes.length > 0;

  const handleVote = async (optionId) => {
    if (hasVoted && !poll.allow_multiple) {
      // Toggle off if same option
      if (myVotes.includes(optionId)) {
        try {
          await axios.post(`${API}/polls/${poll.id}/vote`, { option_ids: [] });
          onVote();
        } catch {}
        return;
      }
    }
    let newVotes;
    if (poll.allow_multiple) {
      newVotes = myVotes.includes(optionId)
        ? myVotes.filter(id => id !== optionId)
        : [...myVotes, optionId];
    } else {
      newVotes = [optionId];
    }
    try {
      await axios.post(`${API}/polls/${poll.id}/vote`, { option_ids: newVotes });
      onVote();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al votar');
    }
  };

  return (
    <div className="glass-panel rounded-lg p-6" data-testid={`poll-${poll.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-11 h-11 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-emerald-50 font-bold">Encuesta</p>
            <p className="text-emerald-100/50 text-sm">{poll.allow_multiple ? 'Opción múltiple' : 'Opción única'} · {timeAgo(poll.created_at)}</p>
          </div>
        </div>
        {user?.role === 'admin' && (
          <button onClick={() => onDelete(poll.id)} className="text-emerald-100/30 hover:text-red-400 transition-colors" data-testid={`poll-delete-${poll.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <p className="text-emerald-50 text-lg font-semibold mb-4">{poll.question}</p>

      <div className="space-y-2 mb-3">
        {poll.options?.map((opt) => {
          const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
          const isSelected = myVotes.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => handleVote(opt.id)}
              className={`w-full text-left rounded-lg p-3 border transition-all relative overflow-hidden ${
                isSelected
                  ? 'border-emerald-500/60 bg-emerald-500/10'
                  : 'border-emerald-500/20 bg-black/30 hover:border-emerald-500/40'
              }`}
              data-testid={`poll-option-${opt.id}`}
            >
              {hasVoted && (
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-500/10 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              )}
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {isSelected && <Check className="h-4 w-4 text-emerald-400" />}
                  <span className="text-emerald-100/90 text-sm">{opt.text}</span>
                </div>
                {hasVoted && (
                  <span className="text-emerald-400 text-sm font-mono font-bold">{pct}%</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-emerald-100/40 text-xs">{totalVotes} voto{totalVotes !== 1 ? 's' : ''}</p>
      <p className="text-emerald-100/30 text-xs mt-1">
        {new Date(poll.created_at).toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })}
      </p>
    </div>
  );
}

export default function Social() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [polls, setPolls] = useState([]);
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => { fetchPosts(); fetchPolls(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/posts`);
      setPosts(res.data);
    } catch { toast.error('Error al cargar publicaciones'); }
  };

  const fetchPolls = async () => {
    try {
      const res = await axios.get(`${API}/polls`);
      setPolls(res.data);
    } catch {}
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(`${BACKEND_URL}${res.data.url}`);
      } catch { toast.error(`Error al subir ${file.name}`); }
    }
    setImages([...images, ...uploaded]);
    setUploading(false);
  };

  const handlePost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/posts`, { content, images });
      setContent('');
      setImages([]);
      fetchPosts();
      toast.success('Publicación creada');
    } catch { toast.error('Error al publicar'); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta publicación?')) return;
    try {
      await axios.delete(`${API}/posts/${id}`);
      fetchPosts();
      toast.success('Publicación eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleDeletePoll = async (id) => {
    if (!confirm('¿Eliminar esta encuesta?')) return;
    try {
      await axios.delete(`${API}/polls/${id}`);
      fetchPolls();
      toast.success('Encuesta eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  // Merge posts and polls into a single feed sorted by date
  const feedItems = [
    ...posts.map(p => ({ ...p, _type: 'post' })),
    ...polls.map(p => ({ ...p, _type: 'poll' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="min-h-screen bg-[#020402] relative">
      <Navbar />
      {/* Brand Watermark */}
      <img src="/brand_logo.png" alt="" className="fixed bottom-4 right-4 w-32 opacity-20 pointer-events-none z-40" />

      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }} data-testid="social-title">
            <span className="flex items-center space-x-3">
              <span>SOCIAL</span>
              <UserBadgesLoader size="md" />
            </span>
          </h1>
          <p className="text-emerald-100/70 mb-8">Publicaciones y actualizaciones del equipo</p>

          {user?.role === 'admin' && (
            <div className="glass-panel rounded-lg p-5 mb-8" data-testid="compose-box">
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Escribe una actualización para la comunidad..." rows={4} className="bg-black/50 border-emerald-500/30 text-emerald-50 mb-3 resize-none" data-testid="compose-textarea" />
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="h-20 w-20 object-cover rounded border border-emerald-500/30" />
                      <button onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="cursor-pointer text-emerald-400 hover:text-emerald-300 flex items-center space-x-2">
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-sm">{uploading ? 'Subiendo...' : 'Agregar imágenes'}</span>
                  <Input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                </label>
                <Button onClick={handlePost} disabled={posting || !content.trim()} className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="compose-submit-btn">
                  <Send className="h-4 w-4 mr-2" /> {posting ? 'Publicando...' : 'Publicar'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-6" data-testid="posts-feed">
            {feedItems.length === 0 ? (
              <div className="glass-panel rounded-lg p-12 text-center">
                <p className="text-emerald-100/50">No hay publicaciones aún</p>
              </div>
            ) : (
              feedItems.map((item) =>
                item._type === 'poll' ? (
                  <PollCard key={`poll-${item.id}`} poll={item} user={user} onDelete={handleDeletePoll} onVote={fetchPolls} />
                ) : (
                  <PostCard key={`post-${item.id}`} post={item} user={user} onDelete={handleDelete} onImageClick={(img) => setZoomImage(img)} />
                )
              )
            )}
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomImage && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoomImage(null)} data-testid="image-zoom-modal">
          <button onClick={() => setZoomImage(null)} className="absolute top-4 right-4 text-white/70 hover:text-white z-10">
            <X className="h-8 w-8" />
          </button>
          <img src={zoomImage} alt="" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
